// /dead-letters — Dead Letter Queue (PAGES.md §3.75)
//
// Platform-level DLQ management. View failed messages, retry, discard.
// ADMIN+ (80). MODERATOR (60) view-only. FR-BACK-009, FR-BACK-010

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PageContainer,
  Button,
  Badge,
  Select,
  Input,
  ConfirmDialog,
  Skeleton,
  EmptyState,
  ErrorBoundary,
  DataTable,
  Pagination,
  Drawer,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { useAuthStore } from '@/stores/use-auth-store';
import { deadLetterService } from '@/services/dead-letter.service';
import type { DeadLetter, DeadLetterStats, DeadLetterStatus } from '@/services/dead-letter.service';
import { ROLE_LEVELS } from '@/types/user';
import { ApiError } from '@/lib/api-client';
import { GLASS, GLASS_FILTER, cn } from '@/constants/design-tokens';
import { deadLetterBadge, isDeadLetterOpen } from './dead-letter-status';
import { selectBulkTargets, bulkConfirmCopy, summariseBulkRun } from './bulk-actions';
import type { BulkAction } from './bulk-actions';

// ─── Constants ─────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

type BadgeVariant = 'success' | 'info' | 'danger' | 'draft' | 'warning' | 'pending';

// 8 RabbitMQ exchanges with color coding (bbpos.sync removed in G2.21 — dead code after G2.17 made SyncService.push synchronous)
const EXCHANGE_COLORS: Record<string, { variant: BadgeVariant; bg: string }> = {
  'bbpos.email': { variant: 'info', bg: 'bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300' },
  'bbpos.notification': { variant: 'pending', bg: 'bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300' },
  'bbpos.backup': { variant: 'success', bg: 'bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300' },
  'bbpos.approval': { variant: 'warning', bg: 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300' },
  'bbpos.audit': { variant: 'draft', bg: 'bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300' },
  'bbpos.report': { variant: 'info', bg: 'bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300' },
  'bbpos.events': { variant: 'pending', bg: 'bg-pink-50 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300' },
  'bbpos.dlx': { variant: 'danger', bg: 'bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300' },
};

const EXCHANGE_FILTER_OPTIONS = [
  { value: '', label: 'All Exchanges' },
  { value: 'bbpos.email', label: 'Email' },
  { value: 'bbpos.notification', label: 'Notification' },
  { value: 'bbpos.backup', label: 'Backup' },
  { value: 'bbpos.approval', label: 'Approval' },
  { value: 'bbpos.audit', label: 'Audit' },
  { value: 'bbpos.report', label: 'Report' },
  { value: 'bbpos.events', label: 'Events' },
  { value: 'bbpos.dlx', label: 'DLX' },
];

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'PENDING', label: 'Failed' },
  { value: 'REPLAYED', label: 'Replayed' },
  { value: 'DISCARDED', label: 'Discarded' },
];

// ─── Icons ─────────────────────────────────────────────────────────────────

function AlertTriangleIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function CheckCircleIcon() {
  return (
    <svg className="h-14 w-14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 11.08V12a10 10 0 11-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatTimestamp(iso: string): string {
  try { return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }); }
  catch { return iso; }
}

function truncate(str: string, maxLen: number): string {
  if (str.length <= maxLen) return str;
  return str.slice(0, maxLen) + '...';
}

function exchangeLabel(exchange: string): string {
  return exchange.replace('bbpos.', '').toUpperCase();
}

// ─── Component ──────────────────────────────────────────────────────────────

function DeadLettersContent() {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const profile = useAuthStore((s) => s.profile);
  const roleLevel = ROLE_LEVELS[profile?.role ?? ''] ?? 0;
  const canRetry = roleLevel >= 80; // ADMIN+
  const canDiscard = roleLevel >= 80;

  const urlExchange = searchParams.get('exchange') ?? '';
  const urlStatus = searchParams.get('status') ?? '';
  const urlFrom = searchParams.get('start_date') ?? '';
  const urlTo = searchParams.get('end_date') ?? '';
  const urlPage = Number(searchParams.get('page') ?? '1');

  const [exchangeFilter, setExchangeFilter] = useState(urlExchange);
  const [statusFilter, setStatusFilter] = useState(urlStatus);
  const [dateFrom, setDateFrom] = useState(urlFrom);
  const [dateTo, setDateTo] = useState(urlTo);
  const [page, setPage] = useState(urlPage);
  const [letters, setLetters] = useState<DeadLetter[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [stats, setStats] = useState<DeadLetterStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedLetter, setSelectedLetter] = useState<DeadLetter | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [discardConfirmId, setDiscardConfirmId] = useState<string | null>(null);
  const [bulkConfirm, setBulkConfirm] = useState<BulkAction | null>(null);
  const [runningBulk, setRunningBulk] = useState<BulkAction | null>(null);

  useEffect(() => { document.title = 'Dead Letters — Orio360 Admin'; }, []);

  useEffect(() => {
    const params: Record<string, string> = {};
    if (exchangeFilter) params.exchange = exchangeFilter;
    if (statusFilter) params.status = statusFilter;
    if (dateFrom) params.start_date = dateFrom;
    if (dateTo) params.end_date = dateTo;
    if (page > 1) params.page = String(page);
    setSearchParams(params, { replace: true });
  }, [exchangeFilter, statusFilter, dateFrom, dateTo, page, setSearchParams]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [listResult, statsResult] = await Promise.all([
        deadLetterService.list({
          page,
          exchange: exchangeFilter || undefined,
          status: (statusFilter || undefined) as DeadLetterStatus | undefined,
          start_date: dateFrom || undefined,
          end_date: dateTo || undefined,
        }),
        deadLetterService.getStats().catch(() => null),
      ]);
      setLetters(listResult.data);
      setTotalItems(listResult.meta.total);
      if (statsResult) setStats(statsResult);
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to load dead letters', variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [page, exchangeFilter, statusFilter, dateFrom, dateTo, toast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRetry = useCallback(async (id: string) => {
    if (!canRetry) return;
    setRetryingId(id);
    try {
      await deadLetterService.retry(id);
      toast({ title: 'Message replayed successfully', variant: 'success' });
      fetchData();
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Retry failed', variant: 'error' });
    } finally {
      setRetryingId(null);
    }
  }, [canRetry, fetchData, toast]);

  const handleDiscard = useCallback(async (id: string) => {
    if (!canDiscard) return;
    try {
      await deadLetterService.discard(id);
      toast({ title: 'Message discarded', variant: 'success' });
      fetchData();
      if (selectedLetter?.id === id) setDrawerOpen(false);
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Discard failed', variant: 'error' });
    }
  }, [canDiscard, fetchData, toast, selectedLetter]);

  // Bulk retry / discard fan out over the listed failed messages — there is no
  // bulk endpoint, so each one goes through the same per-id call as the row action.
  const runBulk = useCallback(async (action: BulkAction) => {
    if (action === 'retry' ? !canRetry : !canDiscard) return;
    const targets = selectBulkTargets(letters);
    if (targets.length === 0) return;
    setRunningBulk(action);
    let succeeded = 0;
    let failed = 0;
    for (const letter of targets) {
      try {
        if (action === 'retry') await deadLetterService.retry(letter.id);
        else await deadLetterService.discard(letter.id);
        succeeded += 1;
      } catch {
        failed += 1;
      }
    }
    setRunningBulk(null);
    setDrawerOpen(false);
    const summary = summariseBulkRun(action, succeeded, failed);
    toast({ title: summary.title, variant: summary.variant });
    fetchData();
  }, [canRetry, canDiscard, letters, fetchData, toast]);

  // The list omits the payload (QA 1.6.15): open the drawer on the row, then
  // swap in GET /dead-letters/:id, which carries it.
  const openDetail = useCallback(async (letter: DeadLetter) => {
    setSelectedLetter(letter);
    setDrawerOpen(true);
    try {
      const detail = await deadLetterService.getById(letter.id);
      setSelectedLetter((cur) => (cur?.id === letter.id ? detail : cur));
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to load message payload', variant: 'error' });
    }
  }, [toast]);

  const columns: ColumnDef<DeadLetter>[] = useMemo(() => [
    {
      key: 'exchange', header: 'Exchange',
      render: (_v: unknown, row: DeadLetter) => {
        const colors = EXCHANGE_COLORS[row.exchange] ?? EXCHANGE_COLORS['bbpos.dlx'];
        return (
          <span className={cn('text-xs font-semibold px-2 py-1 rounded-md', colors.bg)}>
            {exchangeLabel(row.exchange)}
          </span>
        );
      },
    },
    {
      key: 'routing_key', header: 'Routing Key',
      render: (_v: unknown, row: DeadLetter) => (
        <span className="text-xs font-mono text-neutral-600 dark:text-neutral-400">{row.routing_key}</span>
      ),
    },
    {
      key: 'error_message', header: 'Error',
      render: (_v: unknown, row: DeadLetter) => (
        <span className="text-xs text-danger-600 dark:text-danger-400">{truncate(row.error_message, 60)}</span>
      ),
    },
    {
      key: 'created_at', header: 'Time',
      render: (_v: unknown, row: DeadLetter) => (
        <span className="text-xs font-mono tabular-nums text-neutral-400 dark:text-neutral-500">{formatTimestamp(row.created_at)}</span>
      ),
    },
    {
      key: 'retry_count', header: 'Retries', numeric: true,
      render: (_v: unknown, row: DeadLetter) => (
        <span className="text-sm font-mono tabular-nums text-neutral-700 dark:text-neutral-300">{row.retry_count}</span>
      ),
    },
    {
      key: 'status', header: 'Status',
      render: (_v: unknown, row: DeadLetter) => {
        const badge = deadLetterBadge(row);
        return <Badge variant={badge.variant} size="sm">{badge.label}</Badge>;
      },
    },
    {
      key: 'id', header: '',
      render: (_v: unknown, row: DeadLetter) => (
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); openDetail(row); }}
            aria-label="View details"
            className="text-xs font-medium text-brand-600 dark:text-brand-400 hover:text-brand-800 dark:hover:text-brand-200 transition-colors duration-200"
          >
            Details
          </button>
          {canRetry && isDeadLetterOpen(row) && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); handleRetry(row.id); }}
              disabled={retryingId === row.id}
              aria-label="Retry message"
              className="text-xs font-medium text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-200 transition-colors duration-200 ml-2"
            >
              Retry
            </button>
          )}
        </div>
      ),
    },
  ], [openDetail, canRetry, handleRetry, retryingId]);

  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const selectedBadge = selectedLetter ? deadLetterBadge(selectedLetter) : null;
  const bulkTargetCount = useMemo(() => selectBulkTargets(letters).length, [letters]);
  const bulkCopy = bulkConfirm ? bulkConfirmCopy(bulkConfirm, bulkTargetCount) : null;

  return (
    <PageContainer
      title="Dead Letters"
      subtitle="Failed message queue management"
      breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Dead Letters' }]}
      actions={
        canRetry ? (
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={bulkTargetCount === 0 || runningBulk !== null}
              loading={runningBulk === 'retry'}
              onClick={() => setBulkConfirm('retry')}
            >
              <RefreshIcon /><span className="ml-1.5">Retry All{bulkTargetCount > 0 ? ` (${bulkTargetCount})` : ''}</span>
            </Button>
            <Button
              variant="danger"
              size="sm"
              disabled={bulkTargetCount === 0 || runningBulk !== null}
              loading={runningBulk === 'discard'}
              onClick={() => setBulkConfirm('discard')}
            >
              <TrashIcon /><span className="ml-1.5">Discard All{bulkTargetCount > 0 ? ` (${bulkTargetCount})` : ''}</span>
            </Button>
          </div>
        ) : undefined
      }
    >
      <div className="animate-fade-in">
        {/* Header */}
        <div className="bg-gradient-to-r from-rose-600 to-red-700 rounded-2xl p-6 text-white shadow-[0_20px_60px_-20px_rgba(225,29,72,0.5)] mb-6 relative overflow-hidden">
          <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-white/15 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-black/10 blur-3xl" />
          <div className="relative flex items-center gap-3">
            <AlertTriangleIcon className="h-7 w-7 text-rose-200" />
            <div>
              <h1 className="text-2xl font-bold">Dead Letter Queue</h1>
              <p className="text-rose-100 mt-1">Failed messages from all 9 RabbitMQ exchanges — inspect, retry, or discard</p>
            </div>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="flex flex-wrap gap-2 mb-6">
            <div className={cn(GLASS, 'px-4 py-3 animate-slide-up')}>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">Total</p>
              <p className="text-lg font-bold font-mono tabular-nums text-neutral-900 dark:text-neutral-100">{stats.total.toLocaleString('en-IN')}</p>
            </div>
            {Object.entries(stats.by_exchange).map(([exchange, count]) => {
              const colors = EXCHANGE_COLORS[exchange] ?? EXCHANGE_COLORS['bbpos.dlx'];
              return (
                <div key={exchange} className={cn(GLASS, 'px-4 py-3 animate-slide-up')}>
                  <p className={cn('text-xs font-semibold', colors.bg.split(' ').filter((c) => c.startsWith('text-')).join(' '))}>
                    {exchangeLabel(exchange)}
                  </p>
                  <p className="text-sm font-bold font-mono tabular-nums text-neutral-900 dark:text-neutral-100">{count}</p>
                </div>
              );
            })}
          </div>
        )}

        {/* Filter */}
        <div className={cn(GLASS_FILTER, 'p-4 mb-6 flex flex-wrap items-center gap-3')}>
          <div className="w-48">
            <Select
              options={EXCHANGE_FILTER_OPTIONS}
              value={exchangeFilter}
              onChange={(v) => { setExchangeFilter(typeof v === 'string' ? v : v[0] ?? ''); setPage(1); }}
              placeholder="All Exchanges"
            />
          </div>
          <div className="w-40">
            <Select
              options={STATUS_FILTER_OPTIONS}
              value={statusFilter}
              onChange={(v) => { setStatusFilter(typeof v === 'string' ? v : v[0] ?? ''); setPage(1); }}
              placeholder="All Statuses"
            />
          </div>
          <div className="w-40">
            <Input
              type="date"
              size="sm"
              value={dateFrom}
              onChange={(v) => { setDateFrom(v); setPage(1); }}
              aria-label="From date"
            />
          </div>
          <div className="w-40">
            <Input
              type="date"
              size="sm"
              value={dateTo}
              onChange={(v) => { setDateTo(v); setPage(1); }}
              aria-label="To date"
            />
          </div>
        </div>

        {isLoading ? (
          <div className={cn(GLASS, 'p-6 space-y-3')}>
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="flex gap-4">
                <Skeleton variant="rect" width={80} height={22} />
                <Skeleton variant="text" width="15%" height={16} />
                <Skeleton variant="text" width="25%" height={16} />
                <Skeleton variant="text" width="15%" height={16} />
                <Skeleton variant="text" width="5%" height={16} />
              </div>
            ))}
          </div>
        ) : letters.length === 0 ? (
          <EmptyState variant="page" icon={<CheckCircleIcon />} title="No dead letters" description="All messages processed successfully" />
        ) : (
          <>
            <div className={cn(GLASS, 'overflow-hidden mb-6')}>
              <div className="px-5 py-4">
                <h3 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider">
                  Messages ({totalItems.toLocaleString('en-IN')})
                </h3>
              </div>
              <DataTable<DeadLetter> columns={columns} data={letters} />
            </div>
            {totalPages > 1 && <div className="mb-6"><Pagination currentPage={page} totalPages={totalPages} totalItems={totalItems} pageSize={PAGE_SIZE} onPageChange={setPage} /></div>}
          </>
        )}
      </div>

      {/* Detail Drawer */}
      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} title="Dead Letter Detail">
        {selectedLetter && (
          <div className="space-y-4 p-1">
            {/* Meta */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                {(() => {
                  const colors = EXCHANGE_COLORS[selectedLetter.exchange] ?? EXCHANGE_COLORS['bbpos.dlx'];
                  return <span className={cn('text-xs font-semibold px-2 py-1 rounded-md', colors.bg)}>{exchangeLabel(selectedLetter.exchange)}</span>;
                })()}
                {selectedBadge && <Badge variant={selectedBadge.variant} size="sm">{selectedBadge.label}</Badge>}
              </div>
              <div className="text-xs space-y-1 text-neutral-600 dark:text-neutral-400">
                <p><span className="font-medium text-neutral-700 dark:text-neutral-300">Routing Key:</span> <span className="font-mono">{selectedLetter.routing_key}</span></p>
                <p><span className="font-medium text-neutral-700 dark:text-neutral-300">Queue:</span> <span className="font-mono">{selectedLetter.original_queue}</span></p>
                <p><span className="font-medium text-neutral-700 dark:text-neutral-300">Retries:</span> <span className="font-mono tabular-nums">{selectedLetter.retry_count}</span></p>
                <p><span className="font-medium text-neutral-700 dark:text-neutral-300">Failed At:</span> <span className="font-mono tabular-nums">{formatTimestamp(selectedLetter.created_at)}</span></p>
                {selectedLetter.replayed_at && (
                  <p><span className="font-medium text-neutral-700 dark:text-neutral-300">Replayed At:</span> <span className="font-mono tabular-nums">{formatTimestamp(selectedLetter.replayed_at)}</span></p>
                )}
              </div>
            </div>

            {/* Error */}
            <div>
              <h4 className="text-xs font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider mb-2">Error</h4>
              <div className="bg-danger-50 dark:bg-danger-900/20 rounded-lg p-3">
                <p className="text-xs text-danger-700 dark:text-danger-300 font-mono whitespace-pre-wrap break-all">{selectedLetter.error_message}</p>
              </div>
            </div>

            {/* Payload */}
            <div>
              <h4 className="text-xs font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider mb-2">Payload</h4>
              <div className="bg-neutral-50 dark:bg-neutral-900 rounded-lg p-3 overflow-x-auto">
                <pre className="text-xs font-mono text-neutral-700 dark:text-neutral-300 whitespace-pre-wrap break-all">
                  {selectedLetter.payload === undefined ? 'Loading…' : JSON.stringify(selectedLetter.payload, null, 2)}
                </pre>
              </div>
            </div>

            {/* Actions */}
            {canRetry && isDeadLetterOpen(selectedLetter) && (
              <div className="flex gap-2 pt-2">
                <Button variant="primary" size="sm" onClick={() => handleRetry(selectedLetter.id)} loading={retryingId === selectedLetter.id}>
                  <RefreshIcon /><span className="ml-1.5">Retry</span>
                </Button>
                <Button variant="danger" size="sm" onClick={() => setDiscardConfirmId(selectedLetter.id)}>
                  <TrashIcon /><span className="ml-1.5">Discard</span>
                </Button>
              </div>
            )}
          </div>
        )}
      </Drawer>

      <ConfirmDialog
        open={!!discardConfirmId}
        title="Discard Message"
        description="The message is marked Discarded and stays in the list for reference, but it can no longer be retried. This cannot be undone."
        confirmLabel="Discard"
        variant="danger"
        onConfirm={() => {
          if (!discardConfirmId) return;
          const id = discardConfirmId;
          setDiscardConfirmId(null);
          void handleDiscard(id);
        }}
        onCancel={() => setDiscardConfirmId(null)}
      />

      <ConfirmDialog
        open={!!bulkCopy}
        title={bulkCopy?.title ?? ''}
        description={bulkCopy?.description ?? ''}
        confirmLabel={bulkCopy?.confirmLabel}
        variant={bulkCopy?.variant ?? 'danger'}
        onConfirm={() => {
          if (!bulkConfirm) return;
          const action = bulkConfirm;
          setBulkConfirm(null);
          void runBulk(action);
        }}
        onCancel={() => setBulkConfirm(null)}
      />
    </PageContainer>
  );
}

export function DeadLettersPage() {
  return (
    <ErrorBoundary>
      <DeadLettersContent />
    </ErrorBoundary>
  );
}
